import { useEffect } from 'react';

import { Redirect, useLocalSearchParams, useRouter } from 'expo-router';
import { useStoreRequired } from 'features/store/hooks/use-store-required';

import { CardModal } from 'components/card-modal';
import { useMaybeGetLegacyRedirect } from 'components/layout/use-maybe-get-legacy-redirect';
import { LocationEmptyState } from 'components/location-empty-state';
import { toaster } from 'components/toaster/toaster';
import { useExposeRenderReadyOffersRewardsExperiment } from 'experiments/use-render-ready-offers-rewards-experiment';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { useLoyaltyRewardsList } from 'hooks/use-loyalty-rewards-list';
import { useAuthRequiredInModal } from 'pages/authentication/components/auth-required';
import IncentiveDetails from 'pages/loyalty/loyalty-incentives-components/incentive-details/incentive-details';
import LoyaltyLoader from 'pages/loyalty/loyalty-loader/loyalty-loader';
import { selectors, useAppSelector } from 'state/global-state';
import { t } from 'state/intl/translations';
import { IEngineRewardsMap, LoyaltyReward } from 'state/loyalty/types';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { logger } from 'utils/logger';

import { useEnableLoyaltyServiceRewards } from './hooks/use-enable-loyalty-service-rewards';
import { useLoyaltyServiceReward } from './hooks/use-loyalty-service-reward';
import { useEnableServiceRewardsBrowse, useServiceRewards } from './state/service-rewards-context';
import { Reward } from './types';
import { findRewardByRouteId, matchesRewardDetailRoute } from './utils';

export function RewardDetail() {
  // lots of legacy redirects start with /rewards/[notReallyAnID] so we need this here and the 404 handler
  const redirectUrl = useMaybeGetLegacyRedirect(['rewardId']);

  useAuthRequiredInModal({ skip: !!redirectUrl }); // handles deep link to reward w/o being logged in

  const { loyaltyEngineId, rewardId } = useLocalSearchParams<{
    loyaltyEngineId?: string;
    rewardId: string;
  }>();
  const { noStoreSelected } = useStoreRequired(
    true,
    loyaltyEngineId ? { loyaltyEngineId } : undefined
  );

  const { enabled: useServiceRewards, eligibleForExposure } = useEnableLoyaltyServiceRewards();

  // Skip exposure on the legacy-redirect path: this component returns <Redirect />
  // below, so the user never actually sees the reward-detail experiment surface.
  useExposeRenderReadyOffersRewardsExperiment(
    eligibleForExposure && !noStoreSelected && !redirectUrl
  );

  if (redirectUrl) {
    return <Redirect href={redirectUrl} />;
  }

  if (noStoreSelected) {
    return (
      <CardModal alwaysShowHeader>
        <LocationEmptyState title={t('rewards')} message={t('emptyMenuMessage')} />
      </CardModal>
    );
  }

  if (useServiceRewards) {
    return <ServiceRewardDetail loyaltyEngineId={loyaltyEngineId} rewardId={rewardId} />;
  }

  return <LegacyRewardDetail rewardId={rewardId} />;
}

function ServiceRewardDetail({
  loyaltyEngineId,
  rewardId,
}: {
  loyaltyEngineId?: string;
  rewardId: string;
}) {
  const useSingularReward = Boolean(loyaltyEngineId);

  if (useSingularReward) {
    return <SingularServiceRewardDetail loyaltyEngineId={loyaltyEngineId} rewardId={rewardId} />;
  }

  return <ListBackedServiceRewardDetail rewardId={rewardId} />;
}

function SingularServiceRewardDetail({
  loyaltyEngineId,
  rewardId,
}: {
  loyaltyEngineId?: string;
  rewardId: string;
}) {
  const shouldRefetchRewards = useAppSelector(selectors.loyalty.selectShouldRefetchRewards);
  const { reward, engineRewardsMap, error, loading, refreshReward } = useLoyaltyServiceReward({
    loyaltyEngineId,
    skip: false,
  });

  useEffect(() => {
    if (!shouldRefetchRewards) {
      return;
    }

    void refreshReward().catch(error => {
      logger.warn({
        error,
        loyaltyEngineId,
        message: 'Failed to refresh loyalty-service reward detail',
        rewardId,
      });
    });
  }, [loyaltyEngineId, refreshReward, rewardId, shouldRefetchRewards]);

  const matchingReward =
    reward && matchesRewardDetailRoute(reward, rewardId, loyaltyEngineId) ? reward : undefined;

  return (
    <RewardDetailContent
      engineRewardsMap={engineRewardsMap}
      error={error}
      loading={loading}
      selectedReward={matchingReward}
    />
  );
}

function ListBackedServiceRewardDetail({ rewardId }: { rewardId: string }) {
  useEnableServiceRewardsBrowse();
  const { rewardsMap, engineRewardsMap, error, loading } = useServiceRewards();

  return (
    <RewardDetailContent
      engineRewardsMap={engineRewardsMap}
      error={error}
      loading={loading}
      selectedReward={findRewardByRouteId(rewardsMap, rewardId)}
    />
  );
}

function LegacyRewardDetail({ rewardId }: { rewardId: string }) {
  const { sanityRewardsMap, engineRewardsMap, loading } = useLoyaltyRewardsList();

  return (
    <RewardDetailContent
      engineRewardsMap={engineRewardsMap}
      loading={loading || !sanityRewardsMap || !engineRewardsMap}
      selectedReward={sanityRewardsMap?.[rewardId]}
    />
  );
}

function RewardDetailContent({
  engineRewardsMap,
  error,
  loading,
  selectedReward,
}: {
  engineRewardsMap: IEngineRewardsMap;
  error?: Error;
  loading: boolean;
  selectedReward?: LoyaltyReward | Reward;
}) {
  const router = useRouter();
  const isUnifiedMenuEnabled = useEnableUnifiedMenu();
  const { indexedMenu } = useUnifiedMenuContext();
  const isLoading = loading || (isUnifiedMenuEnabled && !indexedMenu.isLoaded());

  useEffect(() => {
    if (error) {
      logger.warn({ error, message: 'Failed to load loyalty-service reward detail' });
    }
  }, [error]);

  useEffect(() => {
    if (!isLoading && !selectedReward) {
      if (!router.canDismiss()) {
        return; // nav is loading still on a deep link
      }
      router.dismiss();
      toaster.negative(error ? t('looksLikeWereExperiencingIssues') : t('nonAvailableReward'));
    }
  }, [error, isLoading, selectedReward, router]);

  if (isLoading || !selectedReward) {
    return (
      <CardModal alwaysShowHeader isLoading disableGlobalAppModals>
        <LoyaltyLoader />
      </CardModal>
    );
  }

  return (
    <IncentiveDetails engineIncentivesMap={engineRewardsMap} selectedIncentive={selectedReward} />
  );
}
